import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ReduxState } from '@/utils/typeHelpers'
import {
  IDocumentsStepContext,
  Document,
  StepContext,
} from '../end-of-year-review/types'
import { fetchWrapper } from '@/reducers/fetch'
import { apiAxios } from '@/api'

/**
 * Redux slice for managing document step contexts in the end-of-year review.
 *
 * Purpose:
 * - Centralize document step context state management
 * - Replace component-level useState calls
 * - Enable parallel API calls instead of sequential
 * - Support optimistic updates for better UX
 */

interface StepContextState {
  data: StepContext | null
  loading: boolean
  error: string | null
}

interface DocumentState {
  stepContexts: {
    [stepId: string]: StepContextState
  }
}

const initialState: DocumentState = {
  stepContexts: {},
}

const documentUploadSlice = createSlice({
  name: 'documentUpload',
  initialState,
  reducers: {
    // Mark all step contexts as loading
    fetchStepContextsStart: (state) => {
      Object.keys(state.stepContexts).forEach((key) => {
        state.stepContexts[key].loading = true
      })
    },

    /**
     * Receive fetched step contexts (success)
     * @param payload - Object mapping stepId to context data
     */
    fetchStepContextsSuccess: (
      state,
      action: PayloadAction<{ [stepId: string]: StepContext }>
    ) => {
      Object.entries(action.payload).forEach(([stepId, context]) => {
        state.stepContexts[stepId] = {
          data: context,
          loading: false,
          error: null,
        }
      })
    },

    /**
     * Handle fetch failure
     * @param payload - Error message
     */
    fetchStepContextsFailure: (state, action: PayloadAction<string>) => {
      Object.keys(state.stepContexts).forEach((key) => {
        state.stepContexts[key].loading = false
        state.stepContexts[key].error = action.payload
      })
    },

    /**
     * Mark a specific step as loading when uploading a document
     * @param payload - Step ID and category type
     */
    documentUploadStart: (
      state,
      action: PayloadAction<{ stepId: string; categoryType: string }>
    ) => {
      if (state.stepContexts[action.payload.stepId]) {
        state.stepContexts[action.payload.stepId].loading = true
      }
    },

    /**
     * Handle successful document upload
     * @param payload - Step ID and uploaded document
     */
    documentUploadSuccess: (
      state,
      action: PayloadAction<{ stepId: string; document: Document }>
    ) => {
      const stepContext = state.stepContexts[action.payload.stepId]

      if (stepContext?.data) {
        stepContext.loading = false

        // Add or update document in the step context
        const existingDocumentIndex = stepContext.data.documents?.findIndex(
          (doc) =>
            doc.categoryCode === action.payload.document.categoryCode &&
            doc.provider === action.payload.document.provider &&
            doc.name === action.payload.document.name
        )

        if (existingDocumentIndex !== undefined && existingDocumentIndex >= 0) {
          // Update existing document
          if (stepContext.data.documents) {
            stepContext.data.documents[existingDocumentIndex] =
              action.payload.document
          }
        } else {
          // Add new document
          stepContext.data.documents = [
            ...(stepContext.data.documents ?? []),
            action.payload.document,
          ]
        }
      }
    },

    /**
     * Handle document upload failure
     * @param payload - Step ID and error message
     */
    documentUploadFailure: (
      state,
      action: PayloadAction<{ stepId: string; error: string }>
    ) => {
      if (state.stepContexts[action.payload.stepId]) {
        state.stepContexts[action.payload.stepId].loading = false
        state.stepContexts[action.payload.stepId].error = action.payload.error
      }
    },

    /**
     * Update a specific step context
     * @param payload - Step ID and updated context
     */
    updateStepContext: (
      state,
      action: PayloadAction<{ stepId: string; context: StepContext }>
    ) => {
      state.stepContexts[action.payload.stepId] = {
        data: action.payload.context,
        loading: false,
        error: null,
      }
    },

    /**
     * Remove a document from a step context
     * @param payload - Step ID, category code, provider, and optional document name
     */
    removeDocument: (
      state,
      action: PayloadAction<{
        stepId: string
        categoryCode: string
        provider?: string
        name?: string
      }>
    ) => {
      const stepContext = state.stepContexts[action.payload.stepId]
      if (stepContext?.data) {
        stepContext.data.documents = stepContext.data.documents?.filter(
          (doc) =>
            // Keep document if any of these conditions are true:
            // - categoryCode doesn't match, OR
            // - provider is specified and doesn't match, OR
            // - name is specified and doesn't match
            doc.categoryCode !== action.payload.categoryCode ||
            (action.payload.provider !== undefined &&
              doc.provider !== action.payload.provider) ||
            (action.payload.name !== undefined &&
              doc.name !== action.payload.name)
        )
      }
    },

    /**
     * Mark a document as "not relevant"
     * @param payload - Step ID and document to mark as not relevant
     */
    markDocumentNotRelevant: (
      state,
      action: PayloadAction<{ stepId: string; document: Document }>
    ) => {
      const stepContext = state.stepContexts[action.payload.stepId]
      if (stepContext?.data) {
        // Find existing document or add new one
        const existingDocumentIndex = stepContext.data.documents?.findIndex(
          (doc) =>
            doc.categoryCode === action.payload.document.categoryCode &&
            doc.provider === action.payload.document.provider &&
            doc.name === action.payload.document.name
        )

        if (existingDocumentIndex !== undefined && existingDocumentIndex >= 0) {
          // Update existing document
          if (stepContext.data.documents) {
            stepContext.data.documents[existingDocumentIndex] =
              action.payload.document
          }
        } else {
          // Add new document
          stepContext.data.documents = [
            ...(stepContext.data.documents ?? []),
            action.payload.document,
          ]
        }
      }
    },
  },
})

export const {
  fetchStepContextsStart,
  fetchStepContextsSuccess,
  fetchStepContextsFailure,
  documentUploadStart,
  documentUploadSuccess,
  documentUploadFailure,
  updateStepContext,
  removeDocument,
  markDocumentNotRelevant,
} = documentUploadSlice.actions

export default documentUploadSlice.reducer

/**
 * Fetch key constants for use with getIsFetching() and getFetchError() selectors
 */
export const FETCH_STEP_CONTEXTS_KEY = 'FETCH_STEP_CONTEXTS_KEY'
export const UPLOAD_DOCUMENT_KEY = (stepId: string) =>
  `UPLOAD_DOCUMENT_KEY_${stepId}`

/**
 * Action creators using fetchWrapper
 * Note: Loading and error states are automatically managed by the fetch slice.
 * Access them using:
 * - useReselector(getIsFetching, FETCH_STEP_CONTEXTS_KEY)
 * - useReselector(getFetchError, FETCH_STEP_CONTEXTS_KEY)
 */

/**
 * Fetch step contexts for multiple steps
 * @param stepIds - Array of step IDs to fetch contexts for
 */
export const fetchStepContexts = (stepIds: string[]) =>
  fetchWrapper({
    fetchKey: FETCH_STEP_CONTEXTS_KEY,
    defaultErrorMessage: 'Error fetching step contexts',
    fetchFunction: async (dispatch) => {
      const response = await apiAxios.get<{
        [stepId: string]: IDocumentsStepContext
      }>('/api/step-contexts', { params: { stepIds } })
      dispatch(fetchStepContextsSuccess(response.data))
      return response.data
    },
  })

/**
 * Upload a document for a specific step
 * @param stepId - Step ID
 * @param document - Document to upload
 * @param categoryType - Category type for the document
 */
export const uploadDocument = (
  stepId: string,
  document: Document,
  categoryType: string
) =>
  fetchWrapper({
    fetchKey: UPLOAD_DOCUMENT_KEY(stepId),
    defaultErrorMessage: 'Error uploading document',
    fetchFunction: async (dispatch) => {
      const response = await apiAxios.post<Document>('/api/documents', {
        stepId,
        document,
        categoryType,
      })
      dispatch(documentUploadSuccess({ stepId, document: response.data }))
      return response.data
    },
  })

/**
 * Selectors
 */

/**
 * Select a specific step context data
 */
export const selectStepContext = (state: ReduxState, stepId: string) =>
  state.documentUpload?.stepContexts?.[stepId]?.data ?? null

/**
 * Select loading state for a specific step
 */
export const selectStepContextLoading = (state: ReduxState, stepId: string) =>
  state.documentUpload?.stepContexts?.[stepId]?.loading ?? false

/**
 * Select error state for a specific step
 */
export const selectStepContextError = (state: ReduxState, stepId: string) =>
  state.documentUpload?.stepContexts?.[stepId]?.error ?? null

/**
 * Select all step contexts
 */
export const selectAllStepContexts = (state: ReduxState) =>
  state.documentUpload?.stepContexts ?? {}

/**
 * Select whether any step is currently loading
 */
export const selectAnyStepLoading = (state: ReduxState) =>
  Object.values(state.documentUpload?.stepContexts ?? {}).some(
    (context) => context.loading
  )
